import { Link } from 'react-router-dom';
import styled from 'styled-components';
import type { ChronumHoursObject, ChronumPayrollCategory } from '@shared/api';
import { ScrollRegion, fleetLeading, fleetType } from '@entities/fleet';
import {
  MACHINE_HOURS,
  MAN_HOURS,
  NBSP,
  UNMAPPED,
  addDecimals,
  categoryColumns,
  formatCoef,
  formatHours,
  formatInt,
  hoursTotals,
  isMachineHourUnit,
  sortHoursObjects,
  targetKey,
  targetKindLabel,
  targetLabel,
} from '@entities/chronum';
import { CellMeta, DataTable, NARROW, cellLink } from './ui';

/** Карточка группы механизаторов в справочнике техники. */
export function groupHref(groupId: string): string {
  return `/agents/econ/fleet?tab=groups&group=${encodeURIComponent(groupId)}`;
}

/** «площадки: БОВ, БОВ-2 и ещё 3». */
function sitesText(sites: readonly { id: string; name: string }[]): string {
  if (sites.length === 0) return 'площадок нет';
  const names = sites.map((s) => s.name);
  const head = names.slice(0, 3).join(', ');
  const rest = names.length - 3;
  return `${names.length === 1 ? 'площадка' : 'площадки'}: ${head}${rest > 0 ? ` и${NBSP}ещё${NBSP}${rest}` : ''}`;
}

const ManTable = styled(DataTable)`
  th.c-name {
    min-width: 200px;
    max-width: 280px;
    font-weight: 400;
  }

  th.c-name > .name {
    display: block;
    font-size: ${fleetType.base};
    font-weight: 500;
    line-height: ${fleetLeading.tight};
    text-wrap: pretty;
    overflow-wrap: anywhere;
  }

  thead th.num {
    min-width: 84px;
    max-width: 120px;
  }

  td.c-total {
    font-weight: 600;
  }

  /* «Без категории» — подписью и линией, не только цветом. */
  th.c-unmapped {
    border-bottom-color: ${({ theme }) => theme.colors.status.warning};
  }

  @container chronum (max-width: ${NARROW}) {
    &,
    & > tbody,
    & > tfoot {
      display: block;
    }

    & > thead {
      display: none;
    }

    & > tbody > tr,
    & > tfoot > tr {
      display: grid;
      grid-template-columns: minmax(0, 1fr);
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    & > tfoot > tr {
      border-bottom: none;
      border-top: 1px solid ${({ theme }) => theme.colors.borderStrong};
    }

    & > tbody > tr > th,
    & > tfoot > tr > th {
      max-width: none;
      padding: ${({ theme }) => `0 0 ${theme.spacing(1.5)}`};
      border: none;
    }

    & > tbody > tr > td,
    & > tfoot > tr > td {
      display: flex;
      justify-content: space-between;
      gap: ${({ theme }) => theme.spacing(3)};
      padding: ${({ theme }) => `${theme.spacing(0.5)} 0`};
      border: none;
    }

    & > tbody > tr > td.empty {
      display: none;
    }

    .narrow-only {
      display: inline;
      white-space: normal;
      text-align: left;
      font-weight: 400;
      color: ${({ theme }) => theme.colors.text.secondary};
    }
  }
`;

interface ManProps {
  objects: readonly ChronumHoursObject[];
  categories: readonly ChronumPayrollCategory[];
  labelledBy: string;
}

/**
 * Чел.-часы по объектам и центрам затрат: категории ФОТ, всего, численность —
 * сумма по площадкам объекта (человек с двух площадок учтён дважды).
 */
export function ManHoursTable({ objects, categories, labelledBy }: ManProps) {
  const sorted = sortHoursObjects(objects);
  const cols = categoryColumns(categories, objects);
  const totals = hoursTotals(objects);
  return (
    <ScrollRegion label="Человеко-часы по объектам — таблица">
      <ManTable aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th scope="col">Объект или центр затрат</th>
            {cols.map((c) => (
              <th
                key={c.code}
                scope="col"
                className={`num${c.code === UNMAPPED ? ' c-unmapped' : ''}`}
              >
                {c.title}
              </th>
            ))}
            <th scope="col" className="num">
              Всего, {MAN_HOURS}
            </th>
            <th scope="col" className="num">
              Человек, сумма по площадкам
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((o) => (
            <tr key={targetKey(o.target)}>
              <th scope="row" className="c-name">
                <span className="name">{targetLabel(o.target)}</span>
                <CellMeta>
                  {targetKindLabel(o.target)} · {sitesText(o.sites)}
                </CellMeta>
              </th>
              {cols.map((c) => {
                const v = o.by_category[c.code];
                return (
                  <td key={c.code} className={`num${v ? '' : ' empty'}`}>
                    <span className="narrow-only">{c.title}</span>
                    {v ? formatHours(v) : '—'}
                  </td>
                );
              })}
              <td className="num c-total">
                <span className="narrow-only">Всего, {MAN_HOURS}</span>
                {formatHours(o.man_hours)}
              </td>
              <td className="num">
                <span className="narrow-only">Человек, сумма по площадкам</span>
                {formatInt(o.workers_sum_by_sites)}
              </td>
            </tr>
          ))}
        </tbody>
        {sorted.length > 1 && (
          <tfoot>
            <tr>
              <th scope="row">Итого по объектам и центрам затрат</th>
              {cols.map((c) => (
                <td key={c.code} className="num">
                  <span className="narrow-only">{c.title}</span>
                  {formatHours(totals.byCategory[c.code] ?? null)}
                </td>
              ))}
              <td className="num c-total">
                <span className="narrow-only">Всего, {MAN_HOURS}</span>
                {formatHours(totals.manHours)}
              </td>
              <td className="num">
                <span className="narrow-only">Человек, сумма по площадкам</span>
                {formatInt(totals.workersSumBySites)}
              </td>
            </tr>
          </tfoot>
        )}
      </ManTable>
    </ScrollRegion>
  );
}

const MachTable = styled(DataTable)`
  tr.group > th {
    padding: ${({ theme }) => `${theme.spacing(5)} ${theme.spacing(3)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    font-weight: 400;
  }

  tbody:first-of-type tr.group > th {
    padding-top: ${({ theme }) => theme.spacing(3)};
  }

  tr.group .title {
    font-size: ${fleetType.small};
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.text.primary};
  }

  tr.group .meta {
    margin-left: ${({ theme }) => theme.spacing(2)};
    font-size: ${fleetType.small};
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
  }

  th.c-group {
    min-width: 220px;
    font-weight: 400;
  }

  th.c-group a {
    ${cellLink}
    font-size: ${fleetType.base};
  }

  td.c-machine {
    font-weight: 600;
  }

  .sign {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  @container chronum (max-width: ${NARROW}) {
    &,
    & > tbody,
    & > tfoot {
      display: block;
    }

    & > thead,
    .wide-only {
      display: none;
    }

    & > tbody > tr,
    & > tfoot > tr {
      display: block;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }

    & > tbody > tr > th,
    & > tbody > tr > td,
    & > tfoot > tr > th,
    & > tfoot > tr > td {
      display: block;
      padding: 0;
      border: none;
    }

    & > tbody > tr:not(.group) {
      padding: ${({ theme }) => `${theme.spacing(2.5)} 0`};
    }

    & > tbody > tr.group {
      border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
    }

    & > tbody > tr.group > th {
      padding: ${({ theme }) => `${theme.spacing(5)} 0 ${theme.spacing(2)}`};
    }

    tr.group .meta {
      display: block;
      margin: 2px 0 0;
      white-space: normal;
    }

    & > tfoot > tr {
      display: flex;
      justify-content: space-between;
      gap: ${({ theme }) => theme.spacing(3)};
      padding: ${({ theme }) => `${theme.spacing(3)} 0`};
      border-bottom: none;
    }

    .narrow-only {
      display: block;
      margin-top: 2px;
      white-space: normal;
      text-align: left;
      font-weight: 400;
    }
  }
`;

interface MachineProps {
  objects: readonly ChronumHoursObject[];
  labelledBy: string;
}

/**
 * Маш.-часы групп механизаторов по объектам: часы механизаторов ×
 * коэффициент группы = маш.-часы — расчёт виден целиком.
 */
export function MachineHoursTable({ objects, labelledBy }: MachineProps) {
  const sorted = sortHoursObjects(objects).filter((o) => o.machine_hours.length > 0);
  const totals = hoursTotals(sorted);
  return (
    <ScrollRegion label="Маш.-часы групп механизаторов — таблица">
      <MachTable aria-labelledby={labelledBy}>
        <thead>
          <tr>
            <th scope="col">Группа механизаторов</th>
            <th scope="col" className="num">
              Часы механизаторов
            </th>
            <th scope="col" className="num">
              Коэффициент
            </th>
            <th scope="col" className="num">
              {`Маш.-${String.fromCharCode(0x2060)}часы`}
            </th>
          </tr>
        </thead>
        {sorted.map((o) => {
          const sum = addDecimals(
            o.machine_hours.filter((m) => isMachineHourUnit(m.unit)).map((m) => m.machine_hours),
          );
          return (
            <tbody key={targetKey(o.target)}>
              <tr className="group">
                <th colSpan={4} scope="rowgroup">
                  <span className="title">{targetLabel(o.target)}</span>
                  <span className="meta">
                    {targetKindLabel(o.target)} · {formatHours(sum)} {MACHINE_HOURS}
                  </span>
                </th>
              </tr>
              {o.machine_hours.map((m) => {
                const perDay = !isMachineHourUnit(m.unit);
                return (
                  <tr key={m.group_id}>
                    <th scope="row" className="c-group">
                      <Link to={groupHref(m.group_id)}>{m.group_name}</Link>
                      {perDay && (
                        <CellMeta>
                          группа учитывается в сутках на объекте — в итог маш.-часов не входит
                        </CellMeta>
                      )}
                      <span className="narrow-only">
                        <CellMeta as="span">
                          {formatHours(m.operator_hours)} ч механизаторов × {formatCoef(m.coef)} ={' '}
                          <strong>
                            {formatHours(m.machine_hours)} {MACHINE_HOURS}
                          </strong>
                        </CellMeta>
                      </span>
                    </th>
                    <td className="num wide-only c-op">{formatHours(m.operator_hours)}</td>
                    <td className="num wide-only">
                      <span className="sign" aria-hidden="true">
                        ×{NBSP}
                      </span>
                      {formatCoef(m.coef)}
                    </td>
                    <td className="num wide-only c-machine">
                      <span className="sign" aria-hidden="true">
                        ={NBSP}
                      </span>
                      {formatHours(m.machine_hours)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          );
        })}
        {sorted.length > 0 && (
          <tfoot>
            <tr>
              <th scope="row" colSpan={3}>
                Итого {MACHINE_HOURS} по объектам
              </th>
              <td className="num">{formatHours(totals.machineHours)}</td>
            </tr>
          </tfoot>
        )}
      </MachTable>
    </ScrollRegion>
  );
}
